import type { QueuedSale } from "./types.js";

/**
 * Anti-tamper hash chain over the offline sales queue (SOW Module 1). Every sale's hash covers its money fields and
 * the previous sale's hash, so editing an amount, deleting a sale or reordering the queue in IndexedDB breaks the
 * chain and the terminal refuses to upload until a supervisor looks at it.
 */
export const GENESIS = "0".repeat(64);

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** JSON with sorted keys, so the same data always hashes the same way. */
export function canonical(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj).filter((k) => obj[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${canonical(obj[k])}`).join(",")}}`;
}

type Sealed = Pick<QueuedSale, "clientRef" | "n" | "seq" | "day" | "createdAt" | "counterCode" | "cashier" | "shiftId" | "cart" | "payments" | "payer" | "total">;

/** The fields a sale's hash protects (sync status fields change later and are not covered). */
export const sealed = (s: Sealed): Sealed => ({
  clientRef: s.clientRef, n: s.n, seq: s.seq, day: s.day, createdAt: s.createdAt, counterCode: s.counterCode, cashier: s.cashier,
  shiftId: s.shiftId, cart: s.cart, payments: s.payments, payer: s.payer, total: s.total,
});

export const chainHash = (prevHash: string, sale: Sealed): Promise<string> => sha256Hex(`${prevHash}\n${canonical(sealed(sale))}`);

export type ChainCheck = { ok: true } | { ok: false; clientRef: string; reason: string };

/**
 * Verify a contiguous run of sales (ordered by n). The first entry's prevHash is the trusted anchor, so the run can
 * start anywhere in the chain.
 */
export async function verifyChain(sales: readonly QueuedSale[]): Promise<ChainCheck> {
  for (const [i, s] of sales.entries()) {
    const prev = sales[i - 1];
    if (prev && (s.n !== prev.n + 1 || s.prevHash !== prev.hash)) return { ok: false, clientRef: s.clientRef, reason: "A sale is missing or out of order." };
    const paid = s.payments.reduce((sum, p) => sum + p.amount, 0);
    if (paid !== s.total) return { ok: false, clientRef: s.clientRef, reason: "Payments do not add up to the bill total." };
    if ((await chainHash(s.prevHash, s)) !== s.hash) return { ok: false, clientRef: s.clientRef, reason: "Sale data was changed after it was saved." };
  }
  return { ok: true };
}
